import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { mainNavigation } from '../../data/navigation'
import { cn } from '../../utils/cn'
import { useLanguage } from '../../contexts/LanguageContext'
import LanguageSwitcher from '../LanguageSwitcher'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const location = useLocation()
  const { t } = useLanguage()
  const lastScrollY = useRef(0)
  const scrollThreshold = 10 // 滚动阈值，避免微小滚动触发

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const scrollDelta = currentScrollY - lastScrollY.current

      // 页面顶部始终显示
      if (currentScrollY < 100) {
        setIsVisible(true)
        lastScrollY.current = currentScrollY
        return
      }

      // 只有滚动超过阈值才触发显示/隐藏
      if (Math.abs(scrollDelta) > scrollThreshold) {
        setIsVisible(scrollDelta < 0) // 向上滚动显示，向下滚动隐藏
        lastScrollY.current = currentScrollY
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const isActiveLink = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 w-full z-50 transition-transform duration-500 ease-out',
        isVisible ? 'translate-y-0' : '-translate-y-full'
      )}
    >
      {/* 渐变背景层 */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 40%, rgba(0,0,0,0.2) 70%, transparent 100%)',
          height: '150%'
        }}
      />

      {/* 导航内容 */}
      <div className="relative z-10 px-6 py-5 md:px-12">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center"
          >
            <img src="/logo.png" alt="xDT" className="h-6" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {mainNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'nav-link whitespace-nowrap',
                  isActiveLink(item.href) && 'text-white nav-link-active'
                )}
              >
                {t(item.name)}
              </Link>
            ))}
          </nav>

          {/* Language Switcher & Mobile Menu */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher />

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-white hover:text-gray-300 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-black/98 backdrop-blur-md z-40">
          <nav className="flex flex-col py-8 px-6">
            {mainNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  'py-4 border-b border-white/10 text-[11px] uppercase tracking-[0.3em] font-medium transition-colors duration-300',
                  isActiveLink(item.href)
                    ? 'text-white'
                    : 'text-gray-500 hover:text-white'
                )}
              >
                {t(item.name)}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  )
}

export default Header
